import React, { useEffect, useState } from 'react';
import { Box, SxProps } from '@mui/material';
import { Header } from 'common/ui/Header';
import { Paths } from 'services/navigation/paths';
import { AnalyseTemplateForm } from 'modules/analyse-templates/components/AnalyseTemplateForm';
import { AnalyseTemplateFormValues } from 'modules/analyse-templates/types/AnalyseTemplateFormValues';
import { validateAnalysisAutomationTasks } from 'modules/analyse/utils/validateAutomationTasks';
import { AutomationTaskInput } from 'generated-graphql/types';
import { ToastType, useToast } from 'services/toast';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useUser } from 'core/stores/hooks/useUser';
import { FailedMessageWithHref } from 'common/ui';
import {
  useGetAutomationTasksTemplateQuery,
  useUpdateAutomationTasksTemplateMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { useGoBack } from 'common/hooks/useGoBack';
import { useParams } from 'react-router-dom';
import { Loading } from 'common/ui/Loading';
import { splitAutomationItem } from 'core/utils/splitAutomationItem';
import { useSetDefaultTemplate } from 'hooks/useSetDefaultTemplate';

const container: SxProps = {
  p: 3
};

export const AnalyseTemplateEditPage = () => {
  const { id } = useParams();
  const { showToast } = useToast();
  const { handleGoBack } = useGoBack(Paths.ANALYSE_TEMPLATES);
  const [initialValues, setInitialValues] =
    useState<AnalyseTemplateFormValues | null>(null);

  const organization = useOrganization();
  const user = useUser();
  const { setDefaultTemplate, organizationConfig } = useSetDefaultTemplate();

  const { data, loading } = useGetAutomationTasksTemplateQuery({
    variables: {
      id: id || ''
    },
    skip: !id
  });

  const [updateAutomationTasksTemplateMutation] =
    useUpdateAutomationTasksTemplateMutation();

  useEffect(() => {
    if (data?.getAutomationTasksTemplate) {
      const template = data.getAutomationTasksTemplate;

      const { rulesTasks, actionTasks } = splitAutomationItem(
        template.automationTasks
      );

      setInitialValues({
        templateName: template.title,
        ruleAutomationTasks: rulesTasks,
        actionAutomationTasks: actionTasks,
        setAsDefault:
          template.id === organizationConfig?.defaultAutomationTaskTemplateId
      });
    }
  }, [data, organizationConfig]);

  const onFormCompleted = async ({
    templateName,
    ruleAutomationTasks,
    actionAutomationTasks,
    setAsDefault
  }: AnalyseTemplateFormValues) => {
    const automationTasks = [...actionAutomationTasks, ...ruleAutomationTasks];

    try {
      const { isDuplicates, validAutomationTasks } =
        validateAnalysisAutomationTasks(automationTasks || [], true);

      if (isDuplicates) {
        showToast({
          message:
            'You have conflicting automations. Remove them before saving.',
          type: ToastType.Error
        });
        return;
      }

      const formattedAutomationTasks =
        validAutomationTasks.map<AutomationTaskInput>((item) => {
          return {
            action: item.action,
            requirementConditions: item.requirementConditions,
            isParent: item.isParent
          };
        });

      const result = await updateAutomationTasksTemplateMutation({
        variables: {
          input: {
            id: id || '',
            title: templateName,
            automationTasks: formattedAutomationTasks,
            organizationId: organization.id,
            userId: user.id
          }
        }
      });

      if (setAsDefault && result.data?.updateAutomationTasksTemplate?.id) {
        await setDefaultTemplate(result.data.updateAutomationTasksTemplate.id);
      }

      handleGoBack();

      showToast({
        title: 'Template updated',
        type: ToastType.Success
      });
    } catch (e) {
      showToast({
        title: 'Something went wrong',
        message: (
          <FailedMessageWithHref
            failedMessage="Try updating the template again."
            emailSubject="Failed to update template"
          />
        ),
        type: ToastType.Error
      });
    }
  };

  if (loading || !initialValues) {
    return <Loading />;
  }

  return (
    <Box sx={container}>
      <Header
        breadcrumbs={[
          { name: 'Analyse', path: Paths.ANALYSE },
          { name: 'Templates', path: Paths.ANALYSE_TEMPLATES }
        ]}
        currentBreadcrumb={initialValues.templateName}
        title={initialValues.templateName}
        items={['Reusable settings for analyses']}
      />
      <AnalyseTemplateForm
        initialValues={initialValues}
        onFormSubmitted={onFormCompleted}
        onCanceled={handleGoBack}
        submitButtonLabel="Save template"
      />
    </Box>
  );
};
